import React, { useState, useCallback, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '../auth/AuthContext';
import { ThemeProvider, createTheme, CssBaseline, Box, CircularProgress } from '@mui/material';
import SignUp from './SignUp';
import Login from './Login';
import Header from './Header';
import Home from './Home';
import GuestHome from './GuestHome';
import '../i18n/i18n';
import { ShimmerProvider } from './ShimmerContext';
import { TutorialProvider } from './TutorialProvider';
import { LanguageProvider } from '../utils/LanguageContext';
import initialModelData from './initialModelData';

const theme = createTheme({
    palette: {
        primary: {
            main: '#1877f2',
        },
        background: {
            default: '#f0f2f5',
        },
    },
});

function AppContent() {
    const [modelData, setModelData] = useState(initialModelData);
    const [currentModel, setCurrentModel] = useState(Object.keys(initialModelData)[0]);
    const [activeStudentModel, setActiveStudentModel] = useState(null);
    const [isInfoPanelOpen, setIsInfoPanelOpen] = useState(false);
    const { currentUser, loading, studentModels } = useAuth();

    // Handle model selection from InstructorSidebar
    const handleModelSelect = useCallback(({ modelName, modelData, studentEmail }) => {
        if (modelData === null) {
            // Return to instructor model
            setActiveStudentModel(null);
            // Use the instructor's version of the current model
            setModelData(prev => ({
                ...prev,
                [modelName]: initialModelData[modelName]
            }));
        } else {
            // Switch to student model
            setActiveStudentModel(studentEmail);
            setModelData(prev => ({
                ...prev,
                [modelName]: modelData
            }));
        }
    }, []);

    useEffect(() => {
        if (currentUser?.role === 'student' && studentModels) {
            setModelData(studentModels);
            setCurrentModel(Object.keys(studentModels)[0]);
        } else if (currentUser?.role === 'instructor' || currentUser?.role === 'explorer') {
            setModelData(initialModelData);
            setCurrentModel(Object.keys(initialModelData)[0]);
        }
    }, [currentUser, studentModels]);



    const handleModelChange = useCallback((newModel) => {
        console.log("Model changed to:", newModel);
        setCurrentModel(newModel);
        // Clear any active student model when changing models
        setActiveStudentModel(null);
    }, []);


    const updateModelData = useCallback((modelName, updatedData) => {
        setModelData(prevData => ({
            ...prevData,
            [modelName]: updatedData
        }));
        console.log("updateModelData called");
    }, []);

    const handleImportedData = useCallback((importedData) => {
        setModelData(prevData => ({
            ...prevData,  // Keep existing models
            ...importedData // Add imported/generated models
        }));
        // Only change to the new model if it's a single import/generation
        if (Object.keys(importedData).length === 1) {
            setCurrentModel(Object.keys(importedData)[0]);
        }
    }, []);

    const toggleInfoPanel = useCallback(() => {
        setIsInfoPanelOpen(prevState => !prevState);
    }, []);

    if (!modelData || !currentModel || loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <CircularProgress />
        </Box>;
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Header
                currentModel={currentModel}
                handleModelChange={handleModelChange}
                modelData={modelData}
                handleImportedData={handleImportedData}
                activeStudentModel={activeStudentModel}
            />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    mt: '60px', // Updated from 120px to match new header height
                    overflow: 'hidden'
                }}
            >
                <Routes>
                    <Route
                        path="/"
                        element={
                            currentUser ? (
                                <Home
                                    currentModel={currentModel}
                                    modelData={modelData[currentModel]}
                                    updateModelData={updateModelData}
                                    isInfoPanelOpen={isInfoPanelOpen}
                                    toggleInfoPanel={toggleInfoPanel}
                                    handleModelSelect={handleModelSelect}
                                    activeStudentModel={activeStudentModel}
                                />
                            ) : (
                                <Navigate to="/guest" replace />
                            )
                        }
                    />
                    <Route
                        path="/login"
                        element={currentUser ? <Navigate to="/" replace /> : <Login />}
                    />
                    <Route
                        path="/signup"
                        element={currentUser ? <Navigate to="/" replace /> : <SignUp />}
                    />
                    <Route
                        path="/guest"
                        element={currentUser ? <Navigate to="/" replace /> : <GuestHome />}
                    />
                </Routes>
            </Box>
        </Box>
    );
}

function App() {
    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <AuthProvider>
                <LanguageProvider>
                    <ShimmerProvider>
                        <TutorialProvider>
                            <Router>
                                <AppContent />
                            </Router>
                        </TutorialProvider>
                    </ShimmerProvider>
                </LanguageProvider>
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;